// API services for IELTS exam operations
import { getToken } from "./tokenStorage";

const BASE_URL = process.env.REACT_APP_API_URL;

const makeAuthenticatedRequest = async (
  url,
  method = "GET",
  body = null,
  timeout = 180000,
) => {
  const token = await getToken();
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      method,
      headers,
      body,
      signal: controller.signal,
    });

    return response;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw err;
  } finally {
    clearTimeout(id);
  }
};

// Upload a single audio file (FormData - no JSON content-type)
const makeAuthenticatedFormDataRequest = async (
  url,
  formData,
  timeout = 180000,
) => {
  const token = await getToken();
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        // Do NOT set Content-Type - browser sets it with boundary for FormData
      },
      body: formData,
      signal: controller.signal,
    });

    return response;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw err;
  } finally {
    clearTimeout(id);
  }
};

// Upload speaking audio file to S3
export const uploadSpeakingAudio = async (
  audioBlob,
  part,
  questionIndex = 0,
  attemptId = "new",
) => {
  try {
    const formData = new FormData();
    const ext = audioBlob.type?.includes("webm") ? "webm" : "mp3";
    formData.append("audio", audioBlob, `${part}_q${questionIndex}.${ext}`);
    formData.append("part", part);
    formData.append("questionIndex", questionIndex.toString());
    formData.append("attemptId", attemptId);

    const response = await makeAuthenticatedFormDataRequest(
      "/ielts_upload/speaking-audio",
      formData,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to upload audio");
    }

    const data = await response.json();
    return { success: true, url: data.url, s3Key: data.s3Key };
  } catch (error) {
    console.error("Error uploading speaking audio:", error);
    return { success: false, error: error.message };
  }
};

// Get IELTS exams with pagination
export const getAllIELTSExams = async ({
  pageNumber = 0,
  pageSize = 10,
  type = "",
  status = "",
} = {}) => {
  try {
    let url = `/ielts_exams?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (type) url += `&type=${type}`;
    if (status) url += `&status=${status}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch IELTS exams");
    }

    const data = await response.json();

    return {
      success: true,
      data: data.ieltsExams || data.data || data.exams || [],
      pagination: data.pagination || {
        pageNumber,
        pageSize,
        totalItems: data.count || data.total || 0,
        totalPages: Math.ceil((data.count || data.total || 0) / pageSize),
        hasNext: (pageNumber + 1) * pageSize < (data.count || data.total || 0),
        hasPrevious: pageNumber > 0,
      },
      message: "IELTS exams retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching IELTS exams:", error);
    return {
      success: false,
      data: [],
      pagination: null,
      error: error.message,
    };
  }
};

// Create IELTS exam
export const createIELTSExam = async (courseId, examData, options = {}) => {
  // Transform data to match backend expectations
  const transformedData = {
    title: examData.title || examData.name,
    type: examData.examType || examData.type || "academic",
    duration: examData.duration || 60,
    total_marks: examData.totalPoints || 100,
    courseId: courseId, // Include courseId in the request
    status: examData.status || "draft", // Include status field
    description: examData.description || "",
    instructions: examData.instructions || "",
    guidelines: examData.guidelines || "",
  };

  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/`,
      "POST",
      JSON.stringify(transformedData),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create IELTS exam");
    }

    const data = await response.json();

    return {
      success: true,
      exam: data.exam || data.ieltsExam || data.data,
      message: "IELTS exam created successfully",
    };
  } catch (error) {
    console.error("Error creating IELTS exam:", error);
    throw new Error(error.message || "Failed to create IELTS exam");
  }
};

// Update IELTS exam
export const updateIELTSExam = async (examId, examData) => {
  // Transform data to match backend expectations
  const transformedData = {
    _id: examId, // Include ID in request body (MongoDB format)
    id: examId, // Also include as 'id' for compatibility
    title: examData.title || examData.name,
    type: examData.examType || examData.type || "academic",
    duration: examData.duration || 60,
    total_marks: examData.totalPoints || 100,
    status: examData.status, // Include status field if provided
    description: examData.description,
    instructions: examData.instructions,
    guidelines: examData.guidelines,
  };

  // Remove undefined values to avoid overwriting with empty
  Object.keys(transformedData).forEach((key) => {
    if (transformedData[key] === undefined) {
      delete transformedData[key];
    }
  });

  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/`,
      "PATCH",
      JSON.stringify(transformedData),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update IELTS exam");
    }

    const data = await response.json();

    return {
      success: true,
      exam: data.exam || data.ieltsExam || data.data,
      message: "IELTS exam updated successfully",
    };
  } catch (error) {
    console.error("Error updating IELTS exam:", error);
    throw new Error(error.message || "Failed to update IELTS exam");
  }
};

// Toggle IELTS exam status (draft/published)
export const toggleIELTSExamStatus = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/toggle-status`,
      "PATCH",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to toggle exam status");
    }

    const data = await response.json();

    return {
      success: true,
      exam: data.ieltsExam || data.exam || data.data,
      message: data.message || "Exam status updated successfully",
    };
  } catch (error) {
    console.error("Error toggling exam status:", error);
    throw new Error(error.message || "Failed to toggle exam status");
  }
};

// Get IELTS exam by ID
export const getIELTSExamById = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}?populate=true`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch IELTS exam");
    }

    const data = await response.json();

    // API returns exam data directly (not wrapped in a key)
    // Check if data has _id property, meaning it's the exam object itself
    const examData = data._id ? data : data.exam || data.ieltsExam || data.data;

    return {
      success: true,
      exam: examData,
      message: "IELTS exam retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching IELTS exam:", error);
    throw new Error(error.message || "Failed to fetch IELTS exam");
  }
};

// Delete IELTS exam
export const deleteIELTSExam = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/`,
      "DELETE",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete IELTS exam");
    }

    return {
      success: true,
      message: "IELTS exam deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting IELTS exam:", error);
    throw new Error(error.message || "Failed to delete IELTS exam");
  }
};

// Publish IELTS exam
export const publishIELTSExam = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/publish`,
      "POST",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to publish IELTS exam");
    }

    const data = await response.json();

    return {
      success: true,
      exam: data.exam || data.ieltsExam || data.data,
      message: "IELTS exam published successfully",
    };
  } catch (error) {
    console.error("Error publishing IELTS exam:", error);
    throw new Error(error.message || "Failed to publish IELTS exam");
  }
};

// Save draft IELTS exam
export const saveDraftIELTSExam = async (
  examData,
  examId = null,
  courseId = null,
) => {
  try {
    if (examId) {
      // Update existing draft
      return await updateIELTSExam(examId, { ...examData, status: "draft" });
    } else {
      // Create new draft - use provided courseId or try to extract from URL
      const effectiveCourseId = courseId || extractCourseIdFromUrl();
      return await createIELTSExam(effectiveCourseId, {
        ...examData,
        status: "draft",
      });
    }
  } catch (error) {
    console.error("Error saving IELTS draft:", error);
    throw new Error(error.message || "Failed to save draft");
  }
};

// Helper to extract courseId from URL
const extractCourseIdFromUrl = () => {
  const path = window.location.pathname;
  // Match patterns like /courses/123/...
  const match = path.match(/\/courses\/([^/]+)/);
  return match ? match[1] : null;
};

// Get IELTS exams by course ID
export const getIELTSExamsByCourseId = async (courseId, { type = "" } = {}) => {
  try {
    let url = `/courses/${courseId}/ielts_exams`;
    if (type) url += `?type=${type}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to fetch course IELTS exams",
      );
    }

    const data = await response.json();

    return {
      success: true,
      exams: data.exams || data.data || [],
      message: "IELTS exams retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching course IELTS exams:", error);
    throw new Error(error.message || "Failed to fetch IELTS exams");
  }
};

// Duplicate IELTS exam
export const duplicateIELTSExam = async (examId, newTitle) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/duplicate`,
      "POST",
      JSON.stringify({ title: newTitle }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to duplicate IELTS exam");
    }

    const data = await response.json();

    return {
      success: true,
      exam: data.exam || data.ieltsExam || data.data,
      message: "IELTS exam duplicated successfully",
    };
  } catch (error) {
    console.error("Error duplicating IELTS exam:", error);
    throw new Error(error.message || "Failed to duplicate IELTS exam");
  }
};

// Get student IELTS exam progress
export const getStudentIELTSExamProgress = async (examId, studentId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/${examId}/progress/${studentId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch student progress");
    }

    const data = await response.json();

    return {
      success: true,
      progress: data.progress || data.data,
    };
  } catch (error) {
    console.error("Error fetching student progress:", error);
    throw new Error(error.message || "Failed to fetch student progress");
  }
};

// Reorder IELTS exams
export const reorderIELTSExams = async (examIds) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exams/reorder`,
      "POST",
      JSON.stringify({ examIds }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to reorder IELTS exams");
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message || "IELTS exams reordered successfully",
    };
  } catch (error) {
    console.error("Error reordering IELTS exams:", error);
    throw new Error(error.message || "Failed to reorder IELTS exams");
  }
};

// ==================== IELTS EXAM SECTIONS API ====================

// Get all sections of an IELTS exam
export const getAllIELTSExamSections = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch exam sections");
    }

    const data = await response.json();

    return {
      success: true,
      sections: data.sections || data.data || [],
      message: "Exam sections retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching exam sections:", error);
    throw new Error(error.message || "Failed to fetch exam sections");
  }
};

// Get a specific section of an IELTS exam
export const getIELTSExamSection = async (examId, sectionId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}/${sectionId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch exam section");
    }

    const data = await response.json();

    return {
      success: true,
      section: data.section || data.data || data,
      message: "Exam section retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching exam section:", error);
    throw new Error(error.message || "Failed to fetch exam section");
  }
};

// Create a new section for an IELTS exam
export const createIELTSExamSection = async (examId, sectionData) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}`,
      "POST",
      JSON.stringify(sectionData),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create exam section");
    }

    const data = await response.json();

    return {
      success: true,
      section: data.section || data.data || data,
      message: "Exam section created successfully",
    };
  } catch (error) {
    console.error("Error creating exam section:", error);
    throw new Error(error.message || "Failed to create exam section");
  }
};

// Update an existing section of an IELTS exam
export const updateIELTSExamSection = async (
  examId,
  sectionId,
  sectionData,
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}/${sectionId}`,
      "PATCH",
      JSON.stringify(sectionData),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update exam section");
    }

    const data = await response.json();

    return {
      success: true,
      section: data.section || data.data || data,
      message: "Exam section updated successfully",
    };
  } catch (error) {
    console.error("Error updating exam section:", error);
    throw new Error(error.message || "Failed to update exam section");
  }
};

// Delete a section from an IELTS exam
export const deleteIELTSExamSection = async (examId, sectionId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}/${sectionId}`,
      "DELETE",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete exam section");
    }

    return {
      success: true,
      message: "Exam section deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting exam section:", error);
    throw new Error(error.message || "Failed to delete exam section");
  }
};

// Reorder sections of an IELTS exam
export const reorderIELTSExamSections = async (examId, sectionIds) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/reorder/${examId}`,
      "POST",
      JSON.stringify({ sectionIds }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to reorder exam sections");
    }

    const data = await response.json();

    return {
      success: true,
      message: data.message || "Exam sections reordered successfully",
    };
  } catch (error) {
    console.error("Error reordering exam sections:", error);
    throw new Error(error.message || "Failed to reorder exam sections");
  }
};

// Add questions to a section of an IELTS exam
export const addQuestionsToIELTSExamSection = async (
  examId,
  sectionId,
  questionIds,
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_sections/${examId}/${sectionId}`,
      "POST",
      JSON.stringify({ questionIds }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to add questions to section",
      );
    }

    const data = await response.json();

    return {
      success: true,
      section: data.section || data.data || data,
      message: "Questions added to section successfully",
    };
  } catch (error) {
    console.error("Error adding questions to section:", error);
    throw new Error(error.message || "Failed to add questions to section");
  }
};

// ==================== IELTS EXAM ATTEMPTS API ====================

// Submit listening section attempt
export const submitListeningAttempt = async (
  classroomId,
  examId,
  answers,
  timeSpent,
  isFullExam = false,
  sectionIndex = 0,
) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (isFullExam) {
      queryParams.append("fullExam", "true");
      queryParams.append("sectionIndex", sectionIndex.toString());
    }
    const queryString = queryParams.toString();
    const url = `/ielts_exam_attempts/${classroomId}/${examId}/listening/submit${queryString ? `?${queryString}` : ""}`;

    const response = await makeAuthenticatedRequest(
      url,
      "POST",
      JSON.stringify({ answers, timeSpent }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to submit listening attempt",
      );
    }

    const data = await response.json();

    return {
      success: true,
      attempt: data.attempt || data.data,
      listeningAttempt: data.listeningAttempt,
      message: "Listening attempt submitted successfully",
    };
  } catch (error) {
    console.error("Error submitting listening attempt:", error);
    throw new Error(error.message || "Failed to submit listening attempt");
  }
};

// Submit reading section attempt
export const submitReadingAttempt = async (
  classroomId,
  examId,
  answers,
  timeSpent,
  isFullExam = false,
  sectionIndex = 0,
) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (isFullExam) {
      queryParams.append("fullExam", "true");
      queryParams.append("sectionIndex", sectionIndex.toString());
    }
    const queryString = queryParams.toString();
    const url = `/ielts_exam_attempts/${classroomId}/${examId}/reading/submit${queryString ? `?${queryString}` : ""}`;

    const response = await makeAuthenticatedRequest(
      url,
      "POST",
      JSON.stringify({ answers, timeSpent }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to submit reading attempt");
    }

    const data = await response.json();

    return {
      success: true,
      attempt: data.attempt || data.data,
      readingAttempt: data.readingAttempt,
      message: "Reading attempt submitted successfully",
    };
  } catch (error) {
    console.error("Error submitting reading attempt:", error);
    throw new Error(error.message || "Failed to submit reading attempt");
  }
};

// Submit writing section attempt
export const submitWritingAttempt = async (
  classroomId,
  examId,
  task1Answer,
  task2Answer,
  timeSpent,
  isFullExam = false,
  sectionIndex = 0,
) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (isFullExam) {
      queryParams.append("fullExam", "true");
      queryParams.append("sectionIndex", sectionIndex.toString());
    }
    const queryString = queryParams.toString();
    const url = `/ielts_exam_attempts/${classroomId}/${examId}/writing/submit${queryString ? `?${queryString}` : ""}`;

    const response = await makeAuthenticatedRequest(
      url,
      "POST",
      JSON.stringify({ task1Answer, task2Answer, timeSpent }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to submit writing attempt");
    }

    const data = await response.json();

    return {
      success: true,
      attempt: data.attempt || data.data,
      writingAttempt: data.writingAttempt,
      message: "Writing attempt submitted successfully",
    };
  } catch (error) {
    console.error("Error submitting writing attempt:", error);
    throw new Error(error.message || "Failed to submit writing attempt");
  }
};

// Submit speaking section attempt (uploads audio to S3, then submits attempt)
export const submitSpeakingAttempt = async (
  classroomId,
  examId,
  recordings,
  timeSpent,
  isFullExam = false,
  sectionIndex = 0,
  speakingData = null,
) => {
  try {
    // Upload audio files to S3 and collect URLs
    const part1Responses = [];
    const part3Responses = [];
    let part2Response = null;

    // Upload Part 1 recordings
    const part1Questions = speakingData?.part1?.questions || [];
    for (let i = 0; i < part1Questions.length; i++) {
      const questionData = part1Questions[i] || null;
      const questionId = questionData?.id;
      const audioBlob = questionId ? recordings?.part1?.[questionId] : null;

      if (!audioBlob) continue;

      const uploadResult = await uploadSpeakingAudio(audioBlob, "part1", i);
      part1Responses.push({
        questionText:
          questionData?.text ||
          questionData?.question ||
          `Part 1 Question ${i + 1}`,
        question: questionData,
        audioUrl: uploadResult.success ? uploadResult.url : "",
        audioS3Key: uploadResult.success ? uploadResult.s3Key : "",
      });
    }

    // Upload Part 2 recording
    if (recordings.part2?.audioBlob) {
      const uploadResult = await uploadSpeakingAudio(
        recordings.part2.audioBlob,
        "part2",
        0,
      );
      part2Response = {
        questionText: speakingData?.part2?.topic || "Part 2 Cue Card",
        question: speakingData?.part2 || null,
        audioUrl: uploadResult.success ? uploadResult.url : "",
        audioS3Key: uploadResult.success ? uploadResult.s3Key : "",
      };
    }

    // Upload Part 3 recordings
    const part3Questions = speakingData?.part3?.questions || [];
    for (let i = 0; i < part3Questions.length; i++) {
      const questionData = part3Questions[i] || null;
      const questionId = questionData?.id;
      const audioBlob = questionId ? recordings?.part3?.[questionId] : null;

      if (!audioBlob) continue;

      const uploadResult = await uploadSpeakingAudio(audioBlob, "part3", i);
      part3Responses.push({
        questionText:
          questionData?.text ||
          questionData?.question ||
          `Part 3 Question ${i + 1}`,
        question: questionData,
        audioUrl: uploadResult.success ? uploadResult.url : "",
        audioS3Key: uploadResult.success ? uploadResult.s3Key : "",
      });
    }

    const submissionData = {
      part1RecordingsCount: part1Responses.length,
      part2Recorded: !!recordings.part2?.audioBlob,
      part3RecordingsCount: part3Responses.length,
      timeSpent,
      part1Responses,
      part2Response,
      part3Responses,
    };

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (isFullExam) {
      queryParams.append("fullExam", "true");
      queryParams.append("sectionIndex", sectionIndex.toString());
    }
    const queryString = queryParams.toString();
    const url = `/ielts_exam_attempts/${classroomId}/${examId}/speaking/submit${queryString ? `?${queryString}` : ""}`;

    const response = await makeAuthenticatedRequest(
      url,
      "POST",
      JSON.stringify(submissionData),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to submit speaking attempt");
    }

    const data = await response.json();

    return {
      success: true,
      attempt: data.attempt || data.data,
      speakingAttempt: data.speakingAttempt,
      message: "Speaking attempt submitted successfully",
    };
  } catch (error) {
    console.error("Error submitting speaking attempt:", error);
    throw new Error(error.message || "Failed to submit speaking attempt");
  }
};

// Regenerate AI remarks for an existing speaking attempt
export const regenerateSpeakingAIResults = async (attemptId) => {
  try {
    const url = `/ielts_exam_attempts/${attemptId}/speaking/regenerate-ai`;
    const response = await makeAuthenticatedRequest(url, "POST");

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to regenerate AI speaking results",
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error regenerating speaking AI results:", error);
    throw new Error(
      error.message || "Failed to regenerate AI speaking results",
    );
  }
};

// Get attempt by ID
export const getAttemptById = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_attempts/${attemptId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch attempt");
    }

    const data = await response.json();

    return {
      success: true,
      attempt: data.attempt || data.data,
      message: "Attempt retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching attempt:", error);
    throw new Error(error.message || "Failed to fetch attempt");
  }
};

// Get in-progress full attempt for resume detection
export const getInProgressAttempt = async (examId, classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_attempts/${examId}/${classroomId}/in-progress`,
    );
    if (!response.ok) return { success: false, attempt: null };
    const data = await response.json();
    return { success: true, attempt: data.attempt };
  } catch (error) {
    console.error("Error fetching in-progress attempt:", error);
    return { success: false, attempt: null };
  }
};

// ==================== IELTS REVIEW API (Teacher/Admin) ====================

// Get all writing/speaking attempts for a classroom
export const getClassroomAttempts = async (
  classroomId,
  { sectionType, gradingStatus, studentSearch, page = 1, limit = 20 } = {},
) => {
  try {
    const params = new URLSearchParams();
    if (sectionType) params.append("sectionType", sectionType);
    if (gradingStatus) params.append("gradingStatus", gradingStatus);
    if (studentSearch) params.append("studentSearch", studentSearch);
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await makeAuthenticatedRequest(
      `/ielts_review/${classroomId}/attempts?${params.toString()}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch classroom attempts");
    }

    const data = await response.json();
    return {
      success: true,
      attempts: data.attempts || [],
      pagination: data.pagination || {},
    };
  } catch (error) {
    console.error("Error fetching classroom attempts:", error);
    return { success: false, attempts: [], error: error.message };
  }
};

// Get full attempt details for teacher review
export const getAttemptForReview = async (classroomId, attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/${classroomId}/attempts/${attemptId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch attempt for review");
    }

    const data = await response.json();
    return {
      success: true,
      attempt: data.attempt,
      writingRemarks: data.writingRemarks,
      speakingRemarks: data.speakingRemarks,
    };
  } catch (error) {
    console.error("Error fetching attempt for review:", error);
    throw new Error(error.message || "Failed to fetch attempt for review");
  }
};

// Submit/update remarks for a section
export const submitRemarks = async (
  attemptId,
  sectionType,
  payload,
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/${attemptId}/${sectionType}/remarks`,
      "POST",
      JSON.stringify(payload),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to submit remarks");
    }

    const data = await response.json();
    return { success: true, remarks: data.remarks, message: data.message };
  } catch (error) {
    console.error("Error submitting remarks:", error);
    throw new Error(error.message || "Failed to submit remarks");
  }
};

// Upload audio remark file
export const uploadAudioRemark = async (
  attemptId,
  sectionType,
  audioBlob,
  targetKey = "general",
) => {
  try {
    const formData = new FormData();
    const ext = audioBlob.type?.includes("webm") ? "webm" : "mp3";
    formData.append("audio", audioBlob, `remark_${targetKey}.${ext}`);
    formData.append("targetKey", targetKey);

    const response = await makeAuthenticatedFormDataRequest(
      `/ielts_review/${attemptId}/${sectionType}/audio-remark`,
      formData,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to upload audio remark");
    }

    const data = await response.json();
    return {
      success: true,
      audioUrl: data.audioUrl,
      audioS3Key: data.audioS3Key,
      remarks: data.remarks,
    };
  } catch (error) {
    console.error("Error uploading audio remark:", error);
    return { success: false, error: error.message };
  }
};

// Publish remarks (makes visible to student, updates band score)
export const publishRemarks = async (attemptId, sectionType) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/${attemptId}/${sectionType}/publish`,
      "PATCH",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to publish remarks");
    }

    const data = await response.json();
    return {
      success: true,
      remarks: data.remarks,
      attempt: data.attempt,
      message: data.message,
    };
  } catch (error) {
    console.error("Error publishing remarks:", error);
    throw new Error(error.message || "Failed to publish remarks");
  }
};

// Get remarks for an attempt section (student or teacher view)
export const getAttemptRemarks = async (attemptId, sectionType) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/${attemptId}/${sectionType}/remarks`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch remarks");
    }

    const data = await response.json();
    return { success: true, remarks: data.remarks };
  } catch (error) {
    console.error("Error fetching remarks:", error);
    return { success: false, remarks: null, error: error.message };
  }
};

// Get count of pending reviews for a classroom (sidebar badge)
export const getPendingReviewCount = async (classroomId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/${classroomId}/pending-count`,
    );
    if (!response.ok) return { count: 0 };
    const data = await response.json();
    return { count: data.count || 0 };
  } catch {
    return { count: 0 };
  }
};

// ==================== PRACTICE WRITING REVIEW API ====================

// Get practice writing attempts for a classroom
export const getPracticeWritingAttempts = async (
  classroomId,
  { gradingStatus, studentSearch, page = 1, limit = 20 } = {},
) => {
  try {
    const params = new URLSearchParams();
    if (gradingStatus) params.append("gradingStatus", gradingStatus);
    if (studentSearch) params.append("studentSearch", studentSearch);
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await makeAuthenticatedRequest(
      `/ielts_review/${classroomId}/practice-attempts?${params.toString()}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch practice writing attempts",
      );
    }

    const data = await response.json();
    return {
      success: true,
      attempts: data.attempts || [],
      pagination: data.pagination || {},
    };
  } catch (error) {
    console.error("Error fetching practice writing attempts:", error);
    return { success: false, attempts: [], error: error.message };
  }
};

// Get full practice attempt details for teacher review
export const getPracticeAttemptForReview = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/practice/${attemptId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch practice attempt");
    }

    const data = await response.json();
    return {
      success: true,
      attempt: data.attempt,
      writingRemarks: data.writingRemarks,
      speakingRemarks: data.speakingRemarks,
    };
  } catch (error) {
    console.error("Error fetching practice attempt for review:", error);
    throw new Error(error.message || "Failed to fetch practice attempt");
  }
};

// Regenerate AI remarks for a practice attempt (teacher action)
export const regeneratePracticeAIRemarks = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/practice/${attemptId}/regenerate-ai`,
      "POST",
      JSON.stringify({}),
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to regenerate remarks");
    }
    const data = await response.json();
    return {
      success: true,
      remarks: data.remarks,
      sectionType: data.sectionType,
    };
  } catch (error) {
    console.error("Error regenerating practice remarks:", error);
    throw new Error(error.message || "Failed to regenerate remarks");
  }
};

// Submit/update remarks for a practice attempt
export const submitPracticeRemarks = async (
  attemptId,
  { bandScores, remarks, generalRemarks },
) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/practice/${attemptId}/remarks`,
      "POST",
      JSON.stringify({ bandScores, remarks, generalRemarks }),
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to submit remarks");
    }

    const data = await response.json();
    return { success: true, remarks: data.remarks, message: data.message };
  } catch (error) {
    console.error("Error submitting practice remarks:", error);
    throw new Error(error.message || "Failed to submit remarks");
  }
};

// Upload audio remark for a practice attempt
export const uploadPracticeAudioRemark = async (
  attemptId,
  audioBlob,
  targetKey = "general",
) => {
  try {
    const formData = new FormData();
    const ext = audioBlob.type?.includes("webm") ? "webm" : "mp3";
    formData.append("audio", audioBlob, `remark_${targetKey}.${ext}`);
    formData.append("targetKey", targetKey);

    const response = await makeAuthenticatedFormDataRequest(
      `/ielts_review/practice/${attemptId}/audio-remark`,
      formData,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to upload audio remark");
    }

    const data = await response.json();
    return {
      success: true,
      audioUrl: data.audioUrl,
      audioS3Key: data.audioS3Key,
      remarks: data.remarks,
    };
  } catch (error) {
    console.error("Error uploading practice audio remark:", error);
    return { success: false, error: error.message };
  }
};

// Publish remarks for a practice attempt
export const publishPracticeRemarks = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/practice/${attemptId}/publish`,
      "PATCH",
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to publish remarks");
    }

    const data = await response.json();
    return {
      success: true,
      remarks: data.remarks,
      attempt: data.attempt,
      message: data.message,
    };
  } catch (error) {
    console.error("Error publishing practice remarks:", error);
    throw new Error(error.message || "Failed to publish remarks");
  }
};

// Get remarks for a practice attempt
export const getPracticeAttemptRemarks = async (attemptId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_review/practice/${attemptId}/remarks`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch remarks");
    }

    const data = await response.json();
    return { success: true, remarks: data.remarks };
  } catch (error) {
    console.error("Error fetching practice remarks:", error);
    return { success: false, remarks: null, error: error.message };
  }
};

// Get user's attempts for an exam
export const getUserAttemptsForExam = async (examId) => {
  try {
    const response = await makeAuthenticatedRequest(
      `/ielts_exam_attempts/exam/${examId}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch attempts");
    }

    const data = await response.json();

    return {
      success: true,
      attempts: data.attempts || data.data || [],
      message: "Attempts retrieved successfully",
    };
  } catch (error) {
    console.error("Error fetching attempts:", error);
    throw new Error(error.message || "Failed to fetch attempts");
  }
};
